// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { SelectPresentationProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductGridContainer, {
    ProductGridContainerStyle,
} from "@turnerBlueprint/App/Components/Product/ProductGridContainer";
import { xIsAuthenticated } from "@turnerBlueprint/App/Store/Selectors/GeneralSelectors";
import { ProductAvailabilityStyles } from "@turnerBlueprint/Overrides/Components/ProductAvailability";
import { mergeWithCss } from "@turnerBlueprint/XDL/Utilities";
import React, { FC, useState } from "react";
import { connect } from "react-redux";

export interface ProductPriceAndActionsProps {
    showAddToList?: boolean;
    showAvailability?: boolean;
    showPrice?: boolean;
    showAddToCartButton?: boolean;
    showCompare?: boolean;
    showAddToCartActions?: boolean;
    extendedStyles?: ProductPriceAndActionsStyle;
    onAddToCartClick?: () => void;
    containerClassName?: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices && state.context.showPricing,
});

type Props = ProductPriceAndActionsProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductPriceAndActionsStyle {
    productGridContainer?: ProductGridContainerStyle;
    priceAndQtyContainer?: InjectableCss;
    productPriceStyles?: ProductPriceStyles;
    priceContainer?: InjectableCss;
    productFields?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    addToListLink?: ProductAddToListLinkStyles;
    productImageStyles?: ProductListProductImageStyles;
    productAvailableStyles?: ProductAvailabilityStyles;
    addToCartActions?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
}

export const productPriceAndActionsStyle: ProductPriceAndActionsStyle = {};

const ProductPriceAndActions: FC<Props> = ({
    showAddToList = true,
    showAvailability = true,
    showPrice = true,
    extendedStyles,
    showAddToCartButton,
    showAddToCartActions, // Show Add to cart + uom + qty
    showCompare = false,
    productContext,
    onAddToCartClick,
    containerClassName,
    canSeePrices,
}) => {
    const [styles] = useState(() => mergeWithCss(productPriceAndActionsStyle, extendedStyles));
    const { product } = productContext;
    return (
        <ProductGridContainer
            extendedStyles={styles.productGridContainer}
            className={containerClassName}
            data-test-selector="product-price-selector"
        >
            <ProductGridContainer.Info>
                <StyledWrapper {...styles.priceAndQtyContainer}>
                    {showPrice && canSeePrices && (
                        <StyledWrapper {...styles.priceContainer}>
                            <ProductPrice
                                product={productContext}
                                showLabel={false}
                                showSavings={true}
                                showPack={false}
                                {...styles.productPriceStyles}
                            />
                        </StyledWrapper>
                    )}
                    {showAddToCartButton && (
                        <>
                            <StyledWrapper {...styles.productFields}>
                                {product.unitOfMeasures && (
                                    <ProductUnitOfMeasureSelect
                                        labelOverride=""
                                        extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                        sizeVariant="small"
                                    />
                                )}
                                <ProductQuantityOrdered />
                            </StyledWrapper>
                        </>
                    )}
                </StyledWrapper>
                {showAvailability && showPrice && (
                    <ProductContextAvailability extendedStyles={styles.productAvailableStyles} />
                )}
            </ProductGridContainer.Info>
            <ProductGridContainer.AddToCartActions>
                {showAvailability && !showPrice && (
                    <ProductContextAvailability extendedStyles={styles.productAvailableStyles} />
                )}
                {showAddToCartButton && (
                    <ProductAddToCartButton
                        extendedStyles={styles.addToCartButton}
                        onAddToCartClick={onAddToCartClick}
                    />
                )}
                {showAddToCartActions && (
                    <>
                        <StyledWrapper {...styles.addToCartActions}>
                            {!product.isVariantParent && (
                                <StyledWrapper {...styles.productFields} className="qty-section">
                                    <ProductQuantityOrdered />
                                    {product.unitOfMeasures && (
                                        <ProductUnitOfMeasureSelect
                                            labelOverride=""
                                            extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                            sizeVariant="small"
                                        />
                                    )}
                                </StyledWrapper>
                            )}
                            <ProductAddToCartButton
                                extendedStyles={styles.addToCartButton}
                                onAddToCartClick={onAddToCartClick}
                            />
                        </StyledWrapper>
                    </>
                )}
            </ProductGridContainer.AddToCartActions>
            <ProductGridContainer.AddToList>
                <ProductListProductImage
                    extendedStyles={styles.productImageStyles}
                    showImage={false}
                    showCompare={showCompare}
                />
                {showAddToList && <ProductAddToListLink extendedStyles={styles.addToListLink} />}
            </ProductGridContainer.AddToList>
        </ProductGridContainer>
    );
};

export default withProductContext(connect(mapStateToProps)(ProductPriceAndActions));
